import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

export default function MarginOfSafetyModal({message} :any) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      {message}<a href="" onClick={handleClickOpen}> Learn how it works</a>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          Margin Of Safety Valuation
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            To calculate the intrinsic value of a business, we need three values:
            <ul>
              <li>The Growth rate</li>
              <li>The Future PE</li>
              <li>The Current EPS</li>
            </ul>
          </Typography>
          <Typography gutterBottom>
            <h3>The Growth Rate</h3>
          </Typography>
          <Typography gutterBottom>
            <p>
              The Growth rate is an estimate of the growth rate of the business for the next ten years.
            </p>
            <p>
              Over the long term, a company's stock price will perform similarly to the economic growth of its underlying business.
              We measure the economic growth of the business by growth in the following areas
              <li>Profits/Net income</li>
              <li>Revenue</li>
              <li>Free cash flow</li>
              <li>Shareholder's equity</li>
            </p>
          </Typography>
          <Typography gutterBottom>
           <p>
            The growth rate is our best guess of the economic growth of the business for the next 10 years.
            We make this informed guess using our knowledge of the business, its industry, and its past historical growth rate numbers.
            The historical growth rates of a stable and predictable business should be consistent and follow a pattern.
            To view the growth rates of a company check the <strong>Historical Growth Rates</strong> section for the following
            <li>Revenue growth rates</li>
            <li>Operating cash flow growth rates</li>
            <li>Net Income growth rates</li>
            <li>Equity growth rates</li>
           </p>
          </Typography>
          <Typography gutterBottom>
            <h5>How to choose a Growth rate</h5>
            <p>The growth rate we choose doesn't have to be 100% accurate it just has to fall within a reasonable ballpark.</p>
            <p>
              <p>Below are some considerations and options to use when choosing a growth rate:</p>
              <p>
                <strong>a. Use the ten years Equity Growth rate (CAGR) as the growth rate</strong><br/>
                Growth in shareholder's equity is a good proxy for the economic growth of a business.
                You can find this number in each stock's <strong>Historical Growth rates</strong> section.
                You can use the oldest Equity Growth Rate available if the company doesn't have up to ten years of history.

                <p>
                  Share buybacks lower shareholder's equity and may cause the company to have negative equity growth.
                  So we cannot use the equity growth rate for companies with lots of share buybacks in their history.
                </p>
              </p>
              <p>
                <strong>b. Use the ten years Operating Cash Growth rate (CAGR) flow as the growth rate.</strong><br/>
                The cash flow growth rate is another excellent proxy for the growth of a business.
                This number is available for each stock in the <strong>Historical Growth rates </strong> section.
                If the company doesn't have up to ten years of history, you can use the oldest operating cash Growth Rate available.
                <p>
                  By default, our automated valuation calculator uses the operating cash flow growth rate of the last 10 years (CAGR) as the growth rate.
                  If the company has less than 10 years of history, it uses the oldest operating cash flow growth rate available.
                </p>
              </p>
              <p>
                <strong>c. Use the analysts' projected five years earnings growth as the growth rate.</strong><br/>
                Analysts spend a lot of time researching companies and their industries, so it's helpful to consider analyst estimates.
                We can use the analysts' five-year projected growth rate as the growth rate.
                To be conservative, we can choose the lower number between analysts' five-year projected growth rate and the ten-year historical operating cash flow growth rate.
                You can find the analysts projected growth rate for free on Yahoo! Finance under the Analysis tab on any stock information page.
              </p>
              <p>
                <strong>d. Use a number that matches a pattern from the historical growth rate numbers.</strong><br/>
                 We can look for a trend in the historical growth rate numbers.
                 We can use that trend and our knowledge of the business and its industry to predict the 10-year growth rate.
                 The Historical Growth Rate numbers are available in the <strong>Historical Growth Rates</strong> section.
              </p>
            </p>
          </Typography>
          <Typography gutterBottom>
            <h5>The problem with very high growth rate numbers</h5>
            <p>
              Companies cannot sustain high growth rates forever. Eventually, every company will start to see a decline in growth.
              By default, our automated valuation calculator limits the growth rate to 20%.
              If the company's 10-year cash flow growth rate (CAGR) exceeds 20%, it defaults to a 20% growth rate.
              When you calculate a valuation, use a growth rate you think is reasonable, a growth rate higher than 20% may be too high.
              It is safer to be conservative in assumptions made in valuations.
              Assuming a growth rate of 15% for a fast-growing, profitable business is a decent middle ground between being defensive and overly aggressive.
              We can check the valuation for possible future outcomes by adjusting the growth rate number.
            </p>
          </Typography>
          <Typography gutterBottom>
            <h3>Future PE of the stock</h3>
            <p>
              The Future PE is an estimate of the PE of the stock in ten years. We use the Future PE to calculate the stock price in 10 years.
              A quick rule of thumb is that the Future PE is two times the growth rate number we have previously selected.
              This is historically true. We can also pick the mean of all PE values of the stock in the last 10 years.
              You can find the historical PE values of companies by searching on <a href="https://www.wolframalpha.com">Wolfram Alpha</a>.
            </p>
            <p>
              You should use your knowledge of the company and its industry to decide if the Future PE number you choose is reasonable for the company.
              The goal is not to be accurate but to fall within a reasonable ballpark.
            </p>
          </Typography>
          <Typography gutterBottom>
           <h5>Be cautious of very high Future PE Ratios.</h5>
           <p>
             Growth companies don't have high PE ratios forever. Eventually, the PE ratio of a high-growth company is going to go down.
             Be careful about using a very high Future PE ratio in a valuation calculation. Our automated valuation calculator limits future PE to 40.
             When choosing a future PE ratio, consider if it is a reasonable one for the company and its industry.
           </p>
          </Typography>
          <Typography gutterBottom>
           <h3>Current EPS of the company (TTM)</h3>
           <p>
            This is the EPS (TTM) for the company. It is used to calculate the fair value of the business.
           </p>
          </Typography>
          <Typography gutterBottom>
            <h3>The Margin of Safety</h3>
            <p>
              The Margin of Safety is the price to buy a stock at 50% off its fair value.
              It is calculated using the following steps:
            </p>
            <p>
              <strong>1. Calculate the Future EPS of the business</strong><br/>
              <p>To calculate the EPS of the business in 10 years, we take the current EPS (TTM) and grow it by our selected growth rate for 10 years.</p>
              <p>The formula is<br/>
                future_eps = (current_eps ⨉ (1 + (growth_rate/100)) ** 10)
              </p>
            </p>
            <p>
              <strong>2. Calculate the Future Stock Price</strong>
              <p>To calculate the price of the stock in 10 years, we multiply the future EPS by the Future PE</p>
              <p>future_stock_price = future_eps * future_pe</p>
            </p>
            <p>
              <strong>3. Calculate the Fair value of the stock</strong>
              <p>To calculate the fair value of the stock today, we discount the future stock price by a minimum rate of return of 15%.
              Since our minimum rate return is always 15%, a rule of thumb to achieve this is by dividing the Future stock price by 4.</p>
              <p>fair_value = future_stock_price / 4</p>
            </p>
            <p>
              <strong>4. Calculate the Margin of Safety Price</strong>
              <p>
                If everything goes well, we will make a 15% return if we buy the stock at fair value.
                We want to buy the stock at a significant discount below fair value.
                This discount is called our margin of safety (MOS). It protects our downside if we are wrong about the company.
                We demand a 50% margin of safety.
                The MOS is calculated by multiplying the fair value by 50%.
              </p>
              <p>margin_of_safety price = fair_value * (50/100)</p>
            </p>
          </Typography>
          <Typography gutterBottom>
            For additional reading see here <a href="https://decode-investing.medium.com/decode-investing-part-3-how-to-calculate-the-true-value-of-a-business-700dbbd0b728">How to calculate the true value of a business</a> and <a href="https://www.amazon.com/Payback-Time-Making-Money-Revenge/dp/0307461866">Payback Time by Phil Town</a>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
}
